/**
 * Auth context — owns "who is the current user?" plus the SSO-config and
 * setup-state snapshots. Lives at the root of the app so every page can
 * read it.
 *
 * On mount it fires three parallel requests:
 *   GET /api/auth/me            → current session's principal (or null)
 *   GET /api/auth/sso/config    → whether SSO is configured (for the login button)
 *   GET /api/auth/setup-state   → { needsSetup, enforced } — drives routing
 *
 * There is NO hard SSO redirect here anymore. Gating is done in-app by
 * `AuthedApp` in App.tsx: unauthenticated + enforced users land on the
 * in-app /auth/login (which offers both local login and an SSO button);
 * needs-setup users land on /auth/setup.
 *
 * The provider also exposes `refresh()` so callers (e.g. the Settings SSO
 * panel after a config save) can force a re-fetch, and `login()` for the
 * local login form.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError } from '../api';
import type {
  AuthMeResponse,
  AuthPrincipal,
  SsoConfigResponse,
  SetupStateResponse,
} from '../types';

export type AuthUser = AuthPrincipal;

interface AuthState {
  user: AuthUser | null;
  ssoConfig: SsoConfigResponse | null;
  setupState: SetupStateResponse | null;
  /** True when the last /me was a 401 with `reason: 'idle-timeout'` — the
   *  session existed but expired for inactivity. The auth gate uses this to
   *  show the "session timed out" banner on the login screen. */
  sessionExpired: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState>({
  user: null,
  ssoConfig: null,
  setupState: null,
  sessionExpired: false,
  loading: true,
  refresh: () => Promise.resolve(),
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

/** Pull the idle-timeout signal out of a failed /me. The backend returns
 *  401 `{ user: null, reason: 'idle-timeout' }`, which `request()` throws as
 *  an ApiError carrying that parsed body. Any other failure (network, 500,
 *  a plain 401) is treated as "no session, not expired". */
function idleTimeoutFromError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  const body = err.body;
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { reason?: unknown }).reason === 'idle-timeout'
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ssoConfig, setSsoConfig] = useState<SsoConfigResponse | null>(null);
  const [setupState, setSetupState] = useState<SetupStateResponse | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Catch /me separately so we can inspect the idle-timeout reason on a
      // 401 before falling back to the anonymous `{ user: null }` shape.
      let expired = false;
      const [me, cfg, setup] = await Promise.all([
        api.authMe().catch((err: unknown) => {
          expired = idleTimeoutFromError(err);
          return { user: null } as AuthMeResponse;
        }),
        api
          .ssoConfigGet()
          .catch((): SsoConfigResponse => ({ configured: false, claimBoundRoles: false, cloud: false })),
        api
          .authSetupState()
          .catch(() => ({ needsSetup: false, enforced: false }) as SetupStateResponse),
      ]);
      setUser(me.user);
      setSsoConfig(cfg);
      setSetupState(setup);
      setSessionExpired(expired);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (username: string, password: string) => {
      const res = await api.authLogin(username, password); // throws ApiError on 401/429/423
      setUser(res.user);
      setSessionExpired(false);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      if (user?.kind === 'sso') {
        const res = await api.authLogoutSso();
        setUser(null);
        if (typeof window !== 'undefined') {
          window.location.href = res.redirectTo ?? '/auth/login';
        }
        return;
      }
      await api.authLogout();
      setUser(null);
      if (typeof window !== 'undefined') {
        window.location.href = '/auth/login';
      }
    } catch {
      /* best-effort — the cookie is likely already gone. */
    }
  }, [user]);

  return (
    <AuthCtx.Provider
      value={{
        user,
        ssoConfig,
        setupState,
        sessionExpired,
        loading,
        refresh,
        login,
        logout,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthCtx);
}
